import { useCallback, useRef, useState } from 'react'
import { URL } from '../gql/client'

export interface LatencyResult {
  wsRtt: number | null
  wsJitter: number | null
  wsMin: number | null
  wsMax: number | null
  httpRtt: number | null
  httpJitter: number | null
  httpMin: number | null
  httpMax: number | null
  measuredAt: number | null
  measuring: boolean
  error: string | null
}

const PING_SAMPLES = 5
const PING_GAP_MS = 30

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

function calcNetworkStats(rtts: number[]): { avg: number; jitter: number; min: number; max: number } {
  const avg = Math.round(rtts.reduce((a, b) => a + b, 0) / rtts.length)
  const min = Math.min(...rtts)
  const max = Math.max(...rtts)
  const jitter =
    rtts.length < 2
      ? 0
      : Math.round(
          rtts.slice(1).reduce((sum, rtt, i) => sum + Math.abs(rtt - rtts[i]), 0) / (rtts.length - 1)
        )
  return { avg, jitter, min, max }
}

export const useLatency = (socket: WebSocket | null) => {
  const [result, setResult] = useState<LatencyResult>({
    wsRtt: null,
    wsJitter: null,
    wsMin: null,
    wsMax: null,
    httpRtt: null,
    httpJitter: null,
    httpMin: null,
    httpMax: null,
    measuredAt: null,
    measuring: false,
    error: null,
  })

  const pendingRef = useRef<{
    timeoutId: ReturnType<typeof setTimeout>
    sentAt: number
  } | null>(null)

  // Single WS ping — returns one RTT value
  const measureWsSingle = useCallback((ws: WebSocket): Promise<number> => {
    return new Promise((resolve, reject) => {
      const onMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type !== 'pong') return
          ws.removeEventListener('message', onMessage)
          const pending = pendingRef.current
          if (!pending) return
          clearTimeout(pending.timeoutId)
          pendingRef.current = null
          resolve(Date.now() - pending.sentAt)
        } catch {
          // non-JSON messages on shared socket — ignore
        }
      }
      const sentAt = Date.now()
      const timeoutId = setTimeout(() => {
        ws.removeEventListener('message', onMessage)
        pendingRef.current = null
        reject(new Error('WebSocket ping timed out'))
      }, 5000)
      pendingRef.current = { timeoutId, sentAt }
      ws.addEventListener('message', onMessage)
      ws.send(JSON.stringify({ type: 'ping', ts: sentAt }))
    })
  }, [])

  // Single HTTP ping — returns one RTT value
  const measureHttpSingle = useCallback(async (): Promise<number> => {
    const start = performance.now()
    const res = await fetch(`${URL}/health/ping`)
    if (!res.ok) throw new Error(`HTTP ping failed: ${res.status}`)
    return Math.round(performance.now() - start)
  }, [])

  const measure = useCallback(async () => {
    if (result.measuring) return
    setResult((p) => ({ ...p, measuring: true, error: null }))

    let wsRtt: number | null = null
    let wsJitter: number | null = null
    let wsMin: number | null = null
    let wsMax: number | null = null
    let httpRtt: number | null = null
    let httpJitter: number | null = null
    let httpMin: number | null = null
    let httpMax: number | null = null
    let error: string | null = null

    // WS: N sequential pings to calculate jitter
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        const rtts: number[] = []
        for (let i = 0; i < PING_SAMPLES; i++) {
          if (i > 0) await sleep(PING_GAP_MS)
          rtts.push(await measureWsSingle(socket))
        }
        const stats = calcNetworkStats(rtts)
        wsRtt = stats.avg
        wsJitter = stats.jitter
        wsMin = stats.min
        wsMax = stats.max
      } catch (e) {
        error = e instanceof Error ? e.message : 'WS ping failed'
      }
    } else {
      error = 'WebSocket not connected'
    }

    // HTTP: N sequential pings to calculate jitter
    try {
      const rtts: number[] = []
      for (let i = 0; i < PING_SAMPLES; i++) {
        if (i > 0) await sleep(PING_GAP_MS)
        rtts.push(await measureHttpSingle())
      }
      const stats = calcNetworkStats(rtts)
      httpRtt = stats.avg
      httpJitter = stats.jitter
      httpMin = stats.min
      httpMax = stats.max
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'HTTP ping failed'
      error = error ? `${error}; ${msg}` : msg
    }

    setResult({
      wsRtt, wsJitter, wsMin, wsMax,
      httpRtt, httpJitter, httpMin, httpMax,
      measuredAt: Date.now(),
      measuring: false,
      error,
    })
  }, [socket, result.measuring, measureWsSingle, measureHttpSingle])

  return { result, measure }
}
